import { useState } from 'react'

import { useMe, useNameAendern, usePasswortAendern } from '../../hooks/queries'
import { useTexte } from '../../texte'
import { PasskeyCard } from './PasskeyCard'
import { fehlertext } from '../../api/client'

/**
 * Was jemand an seinem eigenen Konto ändern darf — Name (#40) und Passwort
 * (#45).
 *
 * **Die E-Mail-Adresse ist ausdrücklich nicht dabei.** Aus #40: „Änderung des
 * Benutzernamens (nicht der E-Mail!)". Sie ist die Anmeldung *und* der Weg
 * zurück bei vergessenem Passwort; sie zu ändern ist eine andere Sache mit
 * anderen Fragen — bestätigen an beide Adressen, sonst sperrt ein Tippfehler
 * das Konto aus.
 *
 * **Zwei Formulare und keine gemeinsame Meldezeile.** Wer den Namen speichert
 * und daneben eine Meldung über das Passwort liest, hat drei Sekunden lang den
 * falschen Schreck.
 */
export function KontoCard() {
  const t = useTexte()
  const me = useMe()

  return (
    <>
      <NameCard />
      <PasswortCard />
      {/* Anmelden ohne Passwort (#21) -- unter dem Passwort, weil es dazukommt
          und es nicht ersetzt. */}
      <PasskeyCard />
      {/* Die Adresse steht da, weil sie zum Konto gehört -- aber ohne Feld:
          was man nicht ändern kann, soll auch nicht wie ein Feld aussehen. */}
      <div className="card card--seite">
        <h2 className="card__titel">{t.kontokarte.email}</h2>
        <p className="card__lead card__lead--dicht">
          {t.kontokarte.emailLead(me.data?.email ?? '')}
        </p>
      </div>
    </>
  )
}

function NameCard() {
  const t = useTexte()
  const me = useMe()
  const aendern = useNameAendern()
  const [name, setName] = useState<string | null>(null)

  /*
   * **Der selbst gewählte Name und nicht der angezeigte.** Eine Verwaltung mit
   * Zweitnamen sieht überall ihren Zweitnamen -- auch am eigenen Konto. Stünde
   * der hier, überschriebe sie beim Speichern ihren eigenen Namen mit dem, den
   * sie sich als Merkhilfe gegeben hat.
   */
  const wert = name ?? me.data?.own_display_name ?? ''
  const sauber = wert.trim()
  const geaendert = sauber !== '' && sauber !== me.data?.own_display_name

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.kontokarte.name}</h2>
      <p className="card__lead">{t.kontokarte.nameLead}</p>

      <label className="field">
        <span className="field__label">{t.kontokarte.nameFeld}</span>
        <input
          className="field__input"
          autoComplete="name"
          maxLength={80}
          value={wert}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      {aendern.isError && (
        <p className="notice" role="alert">
          {fehlertext(aendern.error)}
        </p>
      )}
      {aendern.isSuccess && !geaendert && (
        <p className="card__lead card__lead--absatz">{t.allgemein.gespeichert}</p>
      )}

      <button
        className="button"
        type="button"
        disabled={!geaendert || aendern.isPending}
        onClick={() => aendern.mutate(sauber)}
      >
        {aendern.isPending ? t.allgemein.laeuft : t.kontokarte.nameSpeichern}
      </button>
    </div>
  )
}

function PasswortCard() {
  const t = useTexte()
  const aendern = usePasswortAendern()
  const [alt, setAlt] = useState('')
  const [neu, setNeu] = useState('')
  const [nochmal, setNochmal] = useState('')

  /*
   * **Zweimal eintippen, und der Vergleich steht hier.** Ein Vertipper im
   * neuen Passwort meldet sich sonst erst am nächsten Gerät -- und dann steht
   * man vor einem Konto, dessen Passwort man nicht kennt. Der Server prüft das
   * nicht: er sieht nur eines, und zwei gleiche Felder sind eine Frage der
   * Bedienung, keine Regel.
   */
  const passtNicht = nochmal !== '' && neu !== nochmal
  const bereit = alt !== '' && neu !== '' && neu === nochmal && !aendern.isPending

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.kontokarte.passwort}</h2>
      <p className="card__lead">{t.kontokarte.passwortLead}</p>

      <label className="field">
        <span className="field__label">{t.kontokarte.passwortAlt}</span>
        <input
          className="field__input"
          type="password"
          autoComplete="current-password"
          value={alt}
          onChange={(event) => setAlt(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t.kontokarte.passwortNeu}</span>
        <input
          className="field__input"
          type="password"
          autoComplete="new-password"
          value={neu}
          onChange={(event) => setNeu(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">{t.kontokarte.passwortNochmal}</span>
        <input
          className="field__input"
          type="password"
          autoComplete="new-password"
          value={nochmal}
          onChange={(event) => setNochmal(event.target.value)}
        />
      </label>

      {/* Als Text und nicht als `title`: ein Tooltip ist auf dem Telefon
          unsichtbar. */}
      {passtNicht && (
        <p className="notice" role="alert">{t.kontokarte.passwortUngleich}</p>
      )}
      {aendern.isError && (
        <p className="notice" role="alert">
          {fehlertext(aendern.error)}
        </p>
      )}
      {aendern.isSuccess && (
        <p className="card__lead card__lead--absatz">
          {t.kontokarte.passwortGeaendert}
        </p>
      )}

      <button
        className="button"
        type="button"
        disabled={!bereit}
        onClick={() =>
          aendern.mutate(
            { current_password: alt, new_password: neu },
            {
              // Nach dem Wechsel stehen die Felder leer da -- ein Passwort,
              // das im Feld stehenbleibt, liest der Nächste mit.
              onSuccess: () => {
                setAlt('')
                setNeu('')
                setNochmal('')
              },
            },
          )
        }
      >
        {aendern.isPending ? t.allgemein.laeuft : t.kontokarte.passwort}
      </button>
    </div>
  )
}
